import React, { useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import AsyncSelect from "react-select3/async";

import { customToast as toast } from "../../utils";

const ProjectOwnerSelect = ({
  onChange,
  value: initialValue,
  isLoading,
  isMulti,
  projects,
  users,
  name,
  label,
  placeholder
}) => {
  const [selectLoading, setLoading] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const loadDefaultOptions = useCallback(async () => {
    try {
      const data = users.filter((user) =>
        projects.find(
          (project) =>
            project.project_manager.toString() === user._id.toString()
        )
      );
      setDefaultOptions(
        data.map(({ _id, full_name }) => ({
          value: _id,
          label: full_name
        }))
      );
      setLoading(false);
    } catch (error) {
      toast.error();
    } finally {
      setLoading(false);
    }
  }, [users, projects]);

  useEffect(() => {
    if (
      defaultOptions.length === 0 &&
      !selectLoading &&
      !isLoading &&
      users &&
      Array.isArray(users) &&
      users.length > 0 &&
      projects &&
      Array.isArray(projects) &&
      projects.length > 0
    ) {
      setLoading(true);
      loadDefaultOptions();
    }
  }, [
    loadDefaultOptions,
    selectLoading,
    defaultOptions,
    isLoading,
    users,
    projects
  ]);

  const loadingOptions = useCallback(
    async (inputValue = "") =>
      new Promise((resolve) => {
        try {
          setLoading(true);
          if (
            defaultOptions &&
            Array.isArray(defaultOptions) &&
            defaultOptions.length > 0
          ) {
            const arrayFiltered = defaultOptions.filter((i) =>
              i.label.toLowerCase().includes(inputValue.toLowerCase())
            );
            resolve(arrayFiltered);
          }
          setLoading(false);
        } catch (err) {
          toast.error();
        } finally {
          setLoading(false);
        }
      }),
    [defaultOptions]
  );

  const value = useMemo(() => {
    if (isMulti && Array.isArray(initialValue)) {
      return defaultOptions.filter((x) =>
        initialValue.find((i) => i.value === x.value || i === x.value)
      );
    }
    if (typeof initialValue === "string" && defaultOptions.length > 0) {
      return defaultOptions.find(
        (d) => d.value === initialValue || d === initialValue
      );
    }
    return initialValue;
  }, [defaultOptions, initialValue, isMulti]);

  return (
    <React.Fragment>
      {label ? (
        <label
          className={`control-label ${!label ? "hidden" : ""}`}
          htmlFor={name}
          style={{ textTransform: "uppercase" }}
        >
          {label}
        </label>
      ) : null}
      <AsyncSelect
        id="owner-select"
        name={name}
        className="react-select-container"
        classNamePrefix="react-select-container"
        placeholder={placeholder}
        cacheOptions
        isLoading={selectLoading}
        loadOptions={loadingOptions}
        defaultOptions={defaultOptions}
        onChange={onChange}
        isMulti={isMulti}
        styles={{
          menu: (provided) => ({
            ...provided,
            zIndex: 999
          })
        }}
        value={value}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length ? "No options" : "Start typing..."
        }
      />
    </React.Fragment>
  );
};

ProjectOwnerSelect.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.any,
  isMulti: PropTypes.bool,
  isLoading: PropTypes.bool,
  name: PropTypes.string,
  projects: PropTypes.array,
  users: PropTypes.array,
  placeholder: PropTypes.string,
  label: PropTypes.string
};

ProjectOwnerSelect.defaultProps = {
  isMulti: true,
  projects: null,
  placeholder: "Owner"
};

function mapStatesToProps(state) {
  return {
    users: state.reducers.users,
    loading: state.reducers.loader?.loading || false
  };
}
export default connect(mapStatesToProps, null)(ProjectOwnerSelect);
